import type { SubmitScanRequest } from '@/api/types'
import type { TranslationKey } from '@/i18n/translations'
import type { ScanEventDescription } from './fh-vitals-events'
import type { MindsetPhase } from './mindset-session'
import { ELAPSED, raceElapsed } from './session-timeouts'
import { describeShenAiState } from './shenai-events'
import { toSubmitRequestFromShenAi } from './shenai-result'
import { SHENAI_FACE_STATE, SHENAI_INITIALIZATION_RESULT, SHENAI_MEASUREMENT_STATE } from './shenai-types'
import type { ShenAiVitalClient } from './shenai-types'

/**
 * The phase vocabulary is the OTHER vendor'S, imported rather than restated.
 *
 * Not laziness: the phases are a description of what the PATIENT is waiting for - a download, a
 * licence, a measurement - and that is the same on every vendor. A second identical union would let
 * the two drift, and the screen renders one of them (scanning-screen.tsx reads `MindsetPhase`).
 */
export type ShenAiPhase = MindsetPhase

export interface ShenAiSessionConfiguration {
  /**
   * How long to wait for the vendor's 33 MB runtime to download and instantiate.
   *
   * ITS OWN BUDGET, and a generous one. This was folded in with the licence call under a single
   * 60-second `readyTimeoutMs`, which is a reasonable number for a licence call and a hostile one
   * for 33 MB on a phone: the download alone can outlast it on a cellular connection, and what the
   * patient gets for that is "the scanner could not finish loading" on a scan that was working
   * (docs/decisions.md D-34).
   */
  readonly loadTimeoutMs: number
  /**
   * How long to wait for the vendor's licence call, once the runtime is up.
   *
   * Short, because it is one request to one host and nothing about a slow phone makes it slower.
   */
  readonly licenceTimeoutMs: number
  /**
   * The hard cap on one measurement. Shen ends its own session when its preset's window closes;
   * this is what bounds a session that does not, and it is why the runner calls `stopMeasurement`
   * rather than only waiting for `FINISHED`.
   */
  readonly measureWindowMs: number
  /** How often the vendor's state is read. Shen exposes getters, not events. */
  readonly pollIntervalMs: number
}

export type ShenAiSessionOutcome =
  | { readonly outcome: 'measured'; readonly request: SubmitScanRequest }
  | { readonly outcome: 'no-usable-result' }
  | { readonly outcome: 'error'; readonly messageKey: TranslationKey }

/** Whether a Shen.AI client got as far as being able to measure. */
export type ShenAiPreparation =
  | { readonly prepared: true }
  | { readonly prepared: false; readonly messageKey: TranslationKey }

/**
 * Everything before the measurement: download the runtime, then license it.
 *
 * **Its own function because the hybrid path has to stop here.** A hybrid scan loads both vendors
 * and starts neither until both are ready (docs/decisions.md D-37), which is only expressible if
 * "get ready" and "measure" are two calls. The single-vendor runner below makes them back to back,
 * which is what it always did.
 */
export async function prepareShenAiSession(
  client: ShenAiVitalClient,
  token: string,
  /** The single vendor-side identity every scan of this installation runs as - never the patient's. */
  userId: string,
  config: Pick<ShenAiSessionConfiguration, 'loadTimeoutMs' | 'licenceTimeoutMs'>,
  onProgress?: (fractionComplete: number) => void,
  onPhase?: (phase: ShenAiPhase) => void,
): Promise<ShenAiPreparation> {
  const phase = (next: ShenAiPhase) => {
    // Announced to the screen AND to the console, because a phone has only one of the two and
    // "still loading 33 MB" looks exactly like "stuck" on it (docs/decisions.md D-26).
    console.log('[shenai] phase', next)
    onPhase?.(next)
  }

  // TWO PHASES AND TWO BUDGETS, where there was one of each.
  //
  // The runner used to announce 'loading' and then 'licensing' with nothing in between, and await
  // the client's `initialize` - which did the 33 MB download AND the licence call. So the patient
  // sat on "Almost there" for the entire download, and "Getting the scanner ready. On a phone this
  // can take up to a minute" - the one sentence that tells them what is actually happening and that
  // it is normal - was on screen for a single frame. A scan reported as stuck on "almost there" was
  // a scan downloading perfectly normally (docs/decisions.md D-34).
  //
  // The two budgets are separate for the same reason. D-27 argues for ONE budget across a prologue,
  // because two timeouts that each look reasonable add up to twice as long staring at the same
  // screen - but that argument holds when the screen says the same thing throughout, and it no
  // longer does. These are two waits the patient can tell apart, so they are bounded apart.
  phase('loading')
  const startedLoading = Date.now()

  // The vendor's own account of its download, which is what the bar moves on. Without it the bar
  // has nothing to show until the measurement starts - and a bar that does not move is the whole
  // reason this phase needed a sentence of its own (D-26).
  const loaded = await raceElapsed(
    config.loadTimeoutMs,
    client.load((fractionComplete) => onProgress?.(fractionComplete)),
  )

  if (loaded === ELAPSED) {
    console.warn('[shenai] the vendor runtime never finished loading', {
      afterMs: Date.now() - startedLoading,
    })
    return { prepared: false, messageKey: 'scannerCouldNotLoad' }
  }

  // The bar is handed back to the measurement, which starts from nothing again. Left at 1 it would
  // sit full through the licence call and then jump backwards the moment the vendor starts ticking.
  onProgress?.(0)

  phase('licensing')
  const initialization = await raceElapsed(
    config.licenceTimeoutMs,
    client.initialize(token, userId),
  )

  if (initialization === ELAPSED) {
    console.warn('[shenai] the vendor never answered the licence call', {
      afterMs: Date.now() - startedLoading,
    })
    return { prepared: false, messageKey: 'scannerCouldNotLoad' }
  }

  if (initialization !== SHENAI_INITIALIZATION_RESULT.OK) {
    // The vendor's four-valued verdict, kept apart in the CONSOLE and collapsed into one sentence
    // for the patient. A refused token and an unreachable vendor are the same thing to somebody
    // holding a phone - there is nothing they can do about either - and architecture §9 is that a
    // patient reads a sentence this app wrote, not a vendor enum.
    console.warn('[shenai] the vendor refused to initialize', {
      result: initialization,
      meaning:
        initialization === SHENAI_INITIALIZATION_RESULT.INVALID_API_KEY
          ? 'INVALID_API_KEY - the minted token was rejected'
          : initialization === SHENAI_INITIALIZATION_RESULT.CONNECTION_ERROR
            ? 'CONNECTION_ERROR - the vendor could not be reached from this device'
            : 'INTERNAL_ERROR',
    })
    return { prepared: false, messageKey: 'scanCouldNotStart' }
  }

  return { prepared: true }
}

/**
 * Runs one Shen.AI scan end to end: load → initialize → measure → read → the platform's submit shape.
 *
 * Takes a `ShenAiVitalClient` rather than reaching for the vendor's factory itself, which is what
 * lets every branch below be driven by a fake under Vitest's fake timers instead of by a camera, a
 * 33 MB WASM module and a real minute.
 *
 * **This runner POLLS where the Mindset one subscribes**, because that is the shape of the vendor:
 * Shen exposes `getMeasurementState`, `getFaceState`, `getMeasurementProgressPercentage` and
 * `getMeasurementResults`, and emits nothing. The loop is the adapter between a getter API and the
 * two callbacks this app's screen already understands.
 */
export async function runShenAiScanSession(
  client: ShenAiVitalClient,
  token: string,
  /** The single vendor-side identity every scan of this installation runs as - never the patient's. */
  userId: string,
  config: ShenAiSessionConfiguration,
  onSignals?: (descriptions: ScanEventDescription[]) => void,
  onProgress?: (fractionComplete: number) => void,
  onPhase?: (phase: ShenAiPhase) => void,
): Promise<ShenAiSessionOutcome> {
  try {
    const preparation = await prepareShenAiSession(client, token, userId, config, onProgress, onPhase)

    if (!preparation.prepared) {
      return { outcome: 'error', messageKey: preparation.messageKey }
    }

    console.log('[shenai] phase', 'measuring')
    onPhase?.('measuring')
    client.startMeasurement()

    const finished = await pollShenAiUntilFinished(client, config, onSignals, onProgress)

    if (!finished) {
      // The window closed with the vendor still measuring. Stopping is what makes it finalize
      // whatever it has, which is often a usable reading - so the result is read either way.
      client.stopMeasurement()
    }

    const results = client.results()

    // THE VENDOR'S RAW ANSWER, ON THE CONSOLE, DELIBERATELY - the same decision D-24 records for
    // Mindset. The submit body carries four integers, the service stores what it is sent, and
    // everything the SDK reported ABOUT those numbers exists only in this browser for the length of
    // one scan. It stays on the PATIENT'S OWN device and is never sent anywhere by this line.
    console.log('[shenai] raw vendor result', results)

    const request = toSubmitRequestFromShenAi(results)
    console.log('[shenai] mapped submit request', request ?? null)

    return request ? { outcome: 'measured', request } : { outcome: 'no-usable-result' }
  } catch (error) {
    console.warn('[shenai] the session threw', error)
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  } finally {
    try {
      client.destroy()
    } catch {
      // A vendor that throws while being torn down has already given us whatever it was going to.
    }
  }
}

/**
 * Reads the vendor until it finishes, the window closes, or it fails.
 *
 * EXPORTED, because the hybrid path reads the same vendor the same way (hybrid-session.ts): a
 * getter-shaped SDK polled on a timer is the vendor's shape and not one runner's habit, and a second
 * copy of this loop would be a second place for the FINISHED/FAILED branches and the fraction
 * conversion to drift.
 *
 * @returns true when the vendor reached a terminal state of its own, false when the window closed
 * first - which is what tells the caller whether it still has to ask the vendor to stop.
 */
export async function pollShenAiUntilFinished(
  client: ShenAiVitalClient,
  config: Pick<ShenAiSessionConfiguration, 'measureWindowMs' | 'pollIntervalMs'>,
  onSignals?: (descriptions: ScanEventDescription[]) => void,
  onProgress?: (fractionComplete: number) => void,
): Promise<boolean> {
  const startedMeasuring = Date.now()
  const deadline = startedMeasuring + config.measureWindowMs
  let lastMeasurementState: number | null = null
  let lastFaceState: number | null = null
  let lastWholePercent: number | null = null
  /**
   * Whether this loop has already said out loud that the vendor is not measuring.
   *
   * It exists because THE ABSENCE OF A STATE CHANGE IS THE FAILURE, and a log that only prints on
   * change cannot print it. Twice now a scan has held a patient for its whole window with
   * `measurement: NOT_STARTED` and `face: OK` - once because the SDK was in POSITIONING mode (D-36),
   * once because the preset it was given never starts a measurement (D-40) - and both times the
   * evidence was a log line that stopped rather than a log line that said something. Now it says
   * something.
   */
  let saidItIsNotMeasuring = false
  /** When `startMeasurement()` was last asked, and how many re-asks have been spent - see below. */
  let lastAskedToStart = startedMeasuring
  let retriesSpent = 0

  while (Date.now() < deadline) {
    const measurementState = client.measurementState()

    if (measurementState === SHENAI_MEASUREMENT_STATE.FINISHED) {
      return true
    }

    if (measurementState === SHENAI_MEASUREMENT_STATE.FAILED) {
      console.warn('[shenai] the vendor reported FAILED')
      return true
    }

    // Every signal the vendor is raising, together, which is what the screen shows in red
    // (docs/decisions.md D-28). The runner passes them on and decides nothing about them.
    const faceState = client.faceState()

    // THE VENDOR'S STATE, ON THE CONSOLE, ON EVERY CHANGE - and not on every tick, which at twice a
    // second would bury everything else in the log a patient's phone is being read over a cable.
    //
    // It exists because "the scan produced nothing" has several very different causes that look
    // identical from outside: a face the vendor never found, a signal it never liked, a session that
    // ended early, or a measurement that ran its full window and still had nothing. Only the vendor
    // knows which, it says so in these two enums, and until now nothing in this app wrote them down
    // (docs/decisions.md D-35).
    // The vendor's own account of how far through it is, as a FRACTION - the screen's bar takes one,
    // and the vendor answers a percentage. Guarded rather than trusted, because a NaN width is a bar
    // that vanishes mid-scan; the clamp to 0..1 lives in the component, which is where the number
    // becomes a length.
    const percentage = client.progressPercentage()
    const reported = typeof percentage === 'number' && Number.isFinite(percentage) ? percentage : null
    const wholePercent = reported === null ? null : Math.round(reported)

    // AND THE PROGRESS BESIDE THEM, which it did not carry and had to. A scan was reported as a green
    // card with a bar that never moved, and this app could not say which of two things that was: a
    // vendor answering zero, or a number being dropped between this loop and the screen. The second
    // half is now a test (scan-flow-shenai.test.tsx drives the fraction to `aria-valuenow`), so the
    // console has to answer the first. Whole percent, on change, for the same reason the enums are on
    // change - twice a second for ninety seconds is nine hundred lines to read over a cable.
    if (
      measurementState !== lastMeasurementState
      || faceState !== lastFaceState
      || wholePercent !== lastWholePercent
    ) {
      console.log('[shenai] state', {
        measurement: nameMeasurementState(measurementState),
        face: nameFaceState(faceState),
        progress: wholePercent,
        afterMs: Date.now() - startedMeasuring,
      })
      lastMeasurementState = measurementState
      lastFaceState = faceState
      lastWholePercent = wholePercent
    }

    // NOT_STARTED a few seconds into a measurement is never normal: the vendor moves to
    // WAITING_FOR_FACE or a RUNNING_* state within a tick or two of `startMeasurement()`.
    //
    // SO IT IS ASKED AGAIN before it is called a fault. On an Android tablet the concurrent hybrid
    // asked within ~2 s of handing the camera over - both runtimes warm (D-63) - and the vendor sat at
    // NOT_STARTED with its face OK for the whole window, where the same scan with a minute of preview
    // first measured, and the client documents a second call as harmless (docs/decisions.md D-62).
    //
    // A re-ask made while the face is still UNKNOWN costs nothing from the budget: the vendor's pipeline has
    // not seen a face yet, which is when the tablet's asks were lost (three of them, 4.6 s to 10.6 s, before
    // the fourth took). The budget is for the shape D-36 must still expose - a face it can see and a
    // session that never starts.
    const faceUnknown = faceState === SHENAI_FACE_STATE.UNKNOWN
    if (
      measurementState === SHENAI_MEASUREMENT_STATE.NOT_STARTED
      && (faceUnknown || retriesSpent < MEASUREMENT_START_RETRIES)
      && Date.now() - lastAskedToStart > NOT_MEASURING_IS_A_FAULT_AFTER_MS
    ) {
      console.warn('[shenai] the vendor has not started measuring; asking it again', {
        afterMs: Date.now() - startedMeasuring,
        face: nameFaceState(faceState),
        retriesSpent: faceUnknown ? retriesSpent : retriesSpent + 1,
      })
      client.startMeasurement()
      if (!faceUnknown) retriesSpent += 1
      lastAskedToStart = Date.now()
    } else if (
      !saidItIsNotMeasuring
      && measurementState === SHENAI_MEASUREMENT_STATE.NOT_STARTED
      && retriesSpent >= MEASUREMENT_START_RETRIES
      && Date.now() - lastAskedToStart > NOT_MEASURING_IS_A_FAULT_AFTER_MS
    ) {
      saidItIsNotMeasuring = true
      console.error('[shenai] the vendor has not started measuring; this session will produce nothing', {
        afterMs: Date.now() - startedMeasuring,
        face: nameFaceState(faceState),
        meaning:
          'either the operating mode is not MEASURE (docs/decisions.md D-36) or the measurement '
          + 'preset does not start a session on this build (D-40)',
      })
    }

    onSignals?.(describeShenAiState(measurementState, faceState))

    if (reported !== null) {
      onProgress?.(reported / 100)
    }

    await delay(config.pollIntervalMs)
  }

  return false
}

/**
 * How long a measurement may sit at `NOT_STARTED` before the loop calls it a fault.
 *
 * Three seconds, which is generous: the vendor changes state within a tick or two of being started,
 * and the two real sessions that never measured were still at `NOT_STARTED` forty-five seconds in.
 */
const NOT_MEASURING_IS_A_FAULT_AFTER_MS = 3_000

/**
 * How many more times a vendor still at NOT_STARTED, with a face it can see, is asked to start before the
 * loop calls it a fault. Asks while the face is still UNKNOWN are not counted.
 */
const MEASUREMENT_START_RETRIES = 3

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** The vendor's measurement state, named for the console. */
function nameMeasurementState(value: number): string {
  return (
    Object.entries(SHENAI_MEASUREMENT_STATE).find(([, member]) => member === value)?.[0]
    ?? `UNKNOWN(${value})`
  )
}

/** The vendor's face state, named for the console. */
function nameFaceState(value: number): string {
  return (
    Object.entries(SHENAI_FACE_STATE).find(([, member]) => member === value)?.[0]
    ?? `UNKNOWN(${value})`
  )
}
